import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';

import { useOutlook } from '@hooks/query/useOutlook';
import { useModal } from '@hooks/useModal';
import { Spacing, Typography } from 'chatie-design-system';

import PeekRoomCard from '@components/chat/PeekRoomCard';
import Button from '@components/common/Button';
import Empty from '@components/common/Empty';
import { PaginationIterator } from '@components/common/Pagination';
import Tab from '@components/common/Tab';
import PeekBannerModal from '@components/modal/PeekBannerModal';

import { PlayListLoading } from '@pages/play';

import { OutlookPage, fetchPeekRooms } from '@api/chatbot';
import { fetchUserList } from '@api/user';
import { IChoiceChip } from '@base/outlookModel';
import { Platform, QUERY_KEYS } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IUserDetail } from '@model';
import { useChatPaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

import OutlookComponent from './OutlookComponent';

const groundContainerCSS = css`
  display: flex;
  flex-direction: column;
  margin-top: 42px;

  .tabInTab {
    padding: 16px 12px 20px 12px;
  }

  .chat-enter-pad {
    padding: ${Spacing.small}px ${Spacing.medium}px;
  }

  .empty-btn {
    width: 196px;
    height: 48px;
    margin-top: 20px;
  }

  .outlook-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`;

const peekBannerCSS = (theme: Theme) => css`
  display: flex;
  width: 100%;
  background: ${theme.colors.main50};
  padding: 10px ${Spacing.medium}px;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;

  p {
    ${Typography.body12};
  }
`;

type HomePeekDetailProps = {
  activeTabInTab: string;
  chips: IChoiceChip[];
};

const HomePeekDetail = ({ activeTabInTab, chips }: HomePeekDetailProps) => {
  const { platform } = useGlobalContext();
  const { t } = useTranslation();
  const router = useRouter();
  const { web } = useWebView();

  const peekBannerModalName = `peek-banner-modal`;
  const [
    isPeekBannerModalOpen,
    { open: openPeekBannerModal, close: closePeekBannerModal },
  ] = useModal(peekBannerModalName);

  const selectedChip = chips?.find((chip) => chip.label === activeTabInTab);
  const tabQuery = {
    [selectedChip?.key as string]: selectedChip?.value,
  };
  const query = useChatPaginationQuery(
    QUERY_KEYS.PEEK.OUTLOOK_CODE(activeTabInTab, selectedChip?.key),
    ({ pageParam }) => fetchPeekRooms(tabQuery, pageParam),
    {
      enabled: !!selectedChip,
      suspense: false,
    }
  );

  const [currentPage, setCurrentPage] = useState(-1);

  const dataList = query?.data?.pages.map((v) => v.items);

  const userIdList = useMemo(
    () =>
      (currentPage >= 0 && [
        ...new Set(
          dataList &&
            dataList[currentPage]
              .filter((v) => !v.peek.is_anonymous)
              .map((v) => v.user_id)
        ),
      ]) ||
      [],
    [currentPage]
  );

  const [totalUserList, setTotalUserList] = useState<IUserDetail[][]>([]);

  const { data: userList } = useQuery(
    QUERY_KEYS.USER.LIST(userIdList),
    () => fetchUserList(userIdList),
    {
      enabled: currentPage >= 0,
    }
  );

  const setUserList = useCallback(async () => {
    if (userList) {
      const list = [...totalUserList];
      list.push(userList);
      setTotalUserList(list);
    }
  }, [userList]);

  useEffect(() => {
    if (userList) {
      setUserList();
    }
  }, [setUserList]);

  const handleClickEmpty = () => {
    if (platform === Platform.web) {
      router.push(`/library?tab=chat-library`);
    } else {
      web.openPage(`/library?tab=chat-library`);
    }
  };

  const handleClickBanner = () => {
    openPeekBannerModal();
  };

  const handleClickCard = (id: number) => {
    if (platform === Platform.web) {
      router.push(`/play/chat-rooms/${id}/peek`);
    } else {
      web.openPage(`/play/chat-rooms/${id}/peek`);
    }
  };
  const handleClickProfile = (e: SyntheticEvent, id: number) => {
    e.stopPropagation();
    if (platform === Platform.web) {
      router.push(`/play/characters/${id}`);
    } else {
      web.openPage(`/play/characters/${id}`);
    }
  };

  if (!chips || chips.length === 0) {
    return <PlayListLoading />;
  }

  return (
    <>
      <div css={peekBannerCSS} onClick={handleClickBanner}>
        <p>{t('엿보기가 뭔지 궁금하다면? 여기를 눌러봐요 >')}</p>
      </div>
      <div>
        <PaginationIterator name="peek-room-tab-list" query={query} border>
          {(item, i, pageIndex) => {
            pageIndex > currentPage && setCurrentPage(pageIndex);

            const user =
              (pageIndex >= 0 &&
                totalUserList?.length &&
                totalUserList[pageIndex]?.find(
                  (user) => user.id === item.user_id
                )) ||
              undefined;

            return (
              <PeekRoomCard
                key={i}
                user={user}
                peekInfo={item}
                onClickProfile={handleClickProfile}
                onClickCard={handleClickCard}
              />
            );
          }}
        </PaginationIterator>
      </div>

      <Empty query={query}>
        <>
          <p>{t('캐릭터의 엿보기가 아직 없어요')}</p>
          <p>{t('내가 나눈 대화로 엿보기를 on 해보세요!')}</p>
          <Button
            onClick={handleClickEmpty}
            type={'border'}
            text={t('보관함 내 플레이로 갈래요 👀')}
            className={'empty-btn'}
          />
        </>
      </Empty>
      <PeekBannerModal
        name={peekBannerModalName}
        open={isPeekBannerModalOpen}
        onClose={closePeekBannerModal}
      />
    </>
  );
};

export type HomePeekProps = {
  chips: IChoiceChip[];
};

const HomePeek = ({ chips }: HomePeekProps) => {
  const { t } = useTranslation();
  const tabInTabs = chips.map((chip) => ({
    label: t(chip.key),
    value: t(chip.label),
  }));

  const [activeTabInTab, setActiveTabInTab] = useState<string>(
    tabInTabs[0]?.value
  );
  const outlookTab = chips.filter((item) => item.value === 'outlook');
  const isOutlookTab = outlookTab.find((item) => item.label === activeTabInTab);
  const outlookKey = (isOutlookTab?.key || '') as OutlookPage;

  const handleChangeTab = (tab: string) => {
    if (tab !== activeTabInTab) {
      setActiveTabInTab(tab);
    }
  };

  const tabInTabsInstance = {
    tabs: tabInTabs,
    activeTab: activeTabInTab,
    onChange: handleChangeTab,
  };

  const { usePageOutlook } = useOutlook();
  const outlook = usePageOutlook(outlookKey, { enabled: !!isOutlookTab }, 5);

  useEffect(() => {
    if (chips && chips.length > 0) {
      setActiveTabInTab(chips[0].label);
    }
  }, [chips]);

  return (
    <>
      <div css={groundContainerCSS}>
        <Tab
          className="tabInTab"
          {...tabInTabsInstance}
          type={'badge'}
          hideBorder={true}
        />
        {!!isOutlookTab ? (
          <PaginationIterator
            name="play-outlook-home-peek-all-list"
            query={outlook}
            className={'outlook-container'}
            identifier={'key'}
          >
            {(item, index) => (
              <OutlookComponent component={item} key={index} order={index} />
            )}
          </PaginationIterator>
        ) : (
          <HomePeekDetail chips={chips} activeTabInTab={activeTabInTab} />
        )}
      </div>
    </>
  );
};

export default HomePeek;
